import React, { Component } from "react";
import { withStyles } from "@mui/styles";
import { withTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import DialogContentText from "@mui/material/DialogContentText";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import Grid from "@mui/material/Grid";
import { toast } from "react-toastify";
import BinMappingGeneration from "./BinMappingGeneration";
import LOCATION_TYPE from "../enum/location_type";
import { generateLocationCodesPDF } from "./pdfGenerator";
import SearchableSelect from "../common/SearchableSelect";
import ILocalAGGrid, {
  getDefaultColDef,
  createServerSideDatasource,
  getActionsColumn,
  actionMenuItem,
  foldFiltersToSearchText,
} from "../grid/iLocalAGGrid";

const FilterObj = {
  pageNo: 1,
  pageSize: 20,
  box_id: null,
  search: "",
};

const styles = (theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
  },
  searchContainer: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    alignItems: "flex-end",
    flexWrap: "wrap",
  },
  searchField: {
    flex: 1,
    maxWidth: "400px",
  },
});

class BinMapping extends Component {
  constructor(props) {
    super(props);
    this.state = {
      locationCodes: [],
      loading: false,
      generatingPDF: false,
      deleting: false,
      search: "",
      totalRecords: 0,
      selectedLocation: null,
      deleteDialogOpen: false,
      addLocationDialogOpen: false,
      boxes: [],
      selectedBoxId: null,
      loadingBoxes: false,
      accounts: [],
      accountValues: [],
      selectedAccountId: null,
      loadingAccounts: false,
      editDialogOpen: false,
      editForm: {
        id: null,
        location: "",
        location_code: "",
        color_code: "#f97316",
      },
      savingEdit: false,
      generatePDFDialogOpen: false,
      pdfRangeForm: {
        letterRangeStart: "",
        letterRangeEnd: "",
        numberRangeStart: "",
        numberRangeEnd: "",
      },
      generatingPDFFromRange: false,
    };
    this.searchTimeout = null;
    this.gridApi = null;
  }

  componentDidMount() {
    this.fetchAccounts();
  }

  fetchAccounts = async () => {
    this.setState({ loadingAccounts: true });
    try {
      const token = JSON.parse(sessionStorage.getItem("user")).signInUserSession.idToken.jwtToken;
      
      const response = await fetch(
        process.env.REACT_APP_BASEURL + "accounts",
        {
          method: "GET",
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const accounts = await response.json();
      const accountValues = Array.isArray(accounts) ? accounts.map((account) => {
        return { value: String(account.id), label: account.name };
      }) : [];
      
      this.setState({ 
        accounts: accounts || [],
        accountValues: accountValues,
        loadingAccounts: false 
      }, () => {
        if (accountValues.length > 0 && accountValues[0].value) {
          this.setState({ selectedAccountId: String(accountValues[0].value) }, () => {
            this.fetchBoxes();
          });
        }
      });
    } catch (error) {
      console.error("Error fetching accounts:", error);
      this.setState({ loadingAccounts: false });
      toast.error(this.props.t("BinMapping.ErrorFetchingAccounts") || "Error fetching accounts");
    }
  };

  fetchBoxes = async () => {
    if (!this.state.selectedAccountId) {
      this.setState({ boxes: [], loadingBoxes: false });
      return;
    }

    this.setState({ loadingBoxes: true });
    try {
      const token = JSON.parse(sessionStorage.getItem("user")).signInUserSession.idToken.jwtToken;
      
      const BoxesObj = {
        store_ids: [this.state.selectedAccountId]
      };
      
      const response = await fetch(
        process.env.REACT_APP_BASEURL + "getBoxesByStoreIds",
        {
          method: "POST",
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          body: JSON.stringify(BoxesObj),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        throw new Error(`Failed to parse JSON response: ${jsonError.message}`);
      }
      
      const allBoxes = Array.isArray(data) ? data : [];
      
      const filteredBoxes = allBoxes.filter(box => {
        const locationTypeId = box.location_type_id || (box.type && box.type.id);
        return locationTypeId === LOCATION_TYPE.Counter;
      });
      
      this.setState({ 
        boxes: filteredBoxes,
        loadingBoxes: false,
        selectedBoxId: null,
        locationCodes: [],
        totalRecords: 0,
      }, () => {
        FilterObj.box_id = null;
        FilterObj.search = "";
        if (filteredBoxes.length > 0 && filteredBoxes[0].id) {
          const firstBoxId = String(filteredBoxes[0].id);
          this.setState({ selectedBoxId: firstBoxId }, () => {
            FilterObj.box_id = firstBoxId;
            this.refreshGrid();
          });
        } else {
          this.refreshGrid();
        }
      });
    } catch (error) {
      console.error("Error fetching boxes:", error);
      this.setState({ loadingBoxes: false });
      toast.error(this.props.t("BinMapping.ErrorFetchingBoxes") || "Error fetching boxes");
    }
  };

  refreshGrid = () => {
    if (!this.gridApi) return;
    if (typeof this.gridApi.refreshServerSideStore === "function") {
      this.gridApi.refreshServerSideStore({ purge: true });
    } else if (typeof this.gridApi.refreshServerSide === "function") {
      this.gridApi.refreshServerSide({ purge: true });
    }
  };

  onGridReady = (params) => {
    this.gridApi = params.api;
    FilterObj.box_id = this.state.selectedBoxId || null;
    FilterObj.search = this.state.search || "";
    const datasource = createServerSideDatasource({
      filterBody: FilterObj,
      pageZeroBased: false,
      getPageSize: () => FilterObj.pageSize || 20,
      applyFilterModel: (body, filterModel) => {
        body.search = foldFiltersToSearchText(filterModel) || "";
      },
      fetchPage: (body) => {
        if (!body.box_id) {
          return Promise.resolve({ results: [], total: 0 });
        }
        const token = JSON.parse(sessionStorage.getItem("user")).signInUserSession
          .idToken.jwtToken;
        const requestBody = {
          box_id: body.box_id,
          page: body.pageNo || 1,
          pageSize: body.pageSize || 20,
        };
        if (body.search && String(body.search).trim() !== "") {
          requestBody.search = String(body.search).trim();
        }
        return fetch(process.env.REACT_APP_BASEURL + "deliver/locations", {
          method: "POST",
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          body: JSON.stringify(requestBody),
        })
          .then((response) => {
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            return response.json();
          })
          .then((result) => {
            const data = (result && result.data) || result || {};
            const locations = data.locations || [];
            const total =
              data.total_locations ||
              (data.pagination && data.pagination.total) ||
              locations.length ||
              0;
            return { results: locations, total };
          });
      },
      onSuccess: (results, total) => {
        this.setState({
          locationCodes: results || [],
          totalRecords: total || 0,
          loading: false,
        });
      },
      onError: () => this.setState({ loading: false }),
    });
    if (typeof params.api.setServerSideDatasource === "function") {
      params.api.setServerSideDatasource(datasource);
    }
  };

  onPaginationChanged = (params) => {
    if (!params.api) return;
    const newPageSize = params.api.paginationGetPageSize();
    if (newPageSize && FilterObj.pageSize !== newPageSize) {
      FilterObj.pageSize = newPageSize;
    }
  };

  handleSearchChange = (event) => {
    const value = event.target.value;
    this.setState({ search: value }, () => {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        FilterObj.search = value;
        this.refreshGrid();
      }, 500);
    });
  };

  handleAccountChange = (event) => {
    const accountId = event.target.value ? String(event.target.value) : null;
    FilterObj.box_id = null;
    FilterObj.search = "";
    this.setState({ 
      selectedAccountId: accountId || null,
      selectedBoxId: null,
      search: "",
      locationCodes: [],
      totalRecords: 0,
    }, () => {
      this.refreshGrid();
      if (accountId) {
        this.fetchBoxes();
      }
    });
  };

  handleBoxChange = (event) => {
    const boxId = event.target.value;
    const boxIdString = boxId ? String(boxId) : null;
    FilterObj.box_id = boxIdString;
    FilterObj.search = "";
    this.setState({ 
      selectedBoxId: boxIdString, 
      search: "", 
      locationCodes: [],
      totalRecords: 0,
    }, () => {
      this.refreshGrid();
    });
  };

  getContrastColor = (hexColor) => {
    try {
      if (!hexColor || typeof hexColor !== 'string' || !hexColor.startsWith('#')) {
        return '#000000';
      }
      
      const r = parseInt(hexColor.slice(1, 3), 16);
      const g = parseInt(hexColor.slice(3, 5), 16);
      const b = parseInt(hexColor.slice(5, 7), 16);
      
      if (isNaN(r) || isNaN(g) || isNaN(b)) {
        return '#000000';
      }
      
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminance > 0.5 ? '#000000' : '#FFFFFF';
    } catch (error) {
      return '#000000';
    }
  };

  openRowActionsMenu = (event, params, menuHelpers) => {
    const location = params && params.data;
    // Intentional: skip setState so opening ⋮ does not re-render the page.
    // eslint-disable-next-line react/no-direct-mutation-state
    this.state.selectedLocation = location;
    const items = this.getContextMenuItems(params);
    if (menuHelpers && typeof menuHelpers.openMenu === "function") {
      menuHelpers.openMenu(items);
    }
  };

  getContextMenuItems = (params) => {
    const colId =
      params &&
      params.column &&
      (params.column.getColId ? params.column.getColId() : params.column.colId);
    if (colId !== "actions") return [];
    const { t } = this.props;
    const deleting = this.state.deleting;
    const generatingPDF = this.state.generatingPDF;
    return [
      actionMenuItem({
        name: t("Location.CodeGeneration.Edit") || "Edit",
        icon: "edit",
        disabled: deleting,
        action: () => this.handleEditOpen(),
      }),
      actionMenuItem({
        name: t("Location.CodeGeneration.Delete") || "Delete",
        icon: "delete",
        disabled: deleting,
        action: () => this.handleDelete(),
      }),
      actionMenuItem({
        name: generatingPDF
          ? t("Location.CodeGeneration.GeneratingPDF") || "Generating PDF..."
          : t("Location.CodeGeneration.GeneratePDF") || "Generate PDF",
        icon: "print",
        disabled: generatingPDF,
        action: () => this.handleGeneratePDF(false),
      }),
    ];
  };

  handleDelete = () => {
    if (!this.state.selectedLocation) {
      return;
    }
    const locationToDelete = this.state.selectedLocation;
    this.setState({ 
      deleteDialogOpen: true,
      selectedLocation: locationToDelete 
    });
  };

  handleDeleteDialogClose = () => {
    this.setState({ deleteDialogOpen: false, selectedLocation: null });
  };

  handleEditOpen = () => {
    if (!this.state.selectedLocation) {
      return;
    }
    const { selectedLocation } = this.state;
    this.setState({
      editDialogOpen: true,
      editForm: {
        id: selectedLocation.id,
        location: selectedLocation.location || "",
        location_code: selectedLocation.location_code || "",
        color_code: selectedLocation.color_code || "#f97316",
      },
    });
  };

  handleEditClose = () => {
    this.setState({
      editDialogOpen: false,
      editForm: {
        id: null,
        location: "",
        location_code: "",
        color_code: "#f97316",
      },
      savingEdit: false,
    });
  };

  handleEditFieldChange = (field) => (event) => {
    const value = event.target.value;
    this.setState((prev) => ({
      editForm: { ...prev.editForm, [field]: value },
    }));
  };

  handleEditColorSelect = (color) => {
    this.setState((prev) => ({
      editForm: { ...prev.editForm, color_code: color },
    }));
  };

  handleEditSave = async () => {
    const { editForm, selectedBoxId } = this.state;
    if (!editForm.id || !editForm.location || !editForm.location_code) {
      toast.error(this.props.t("Location.CodeGeneration.CodeRequired") || "All fields are required");
      return;
    }
    this.setState({ savingEdit: true });
    try {
      const token = JSON.parse(sessionStorage.getItem("user")).signInUserSession.idToken.jwtToken;
      const payload = {
        id: editForm.id,
        box_id: selectedBoxId,
        location: editForm.location,
        location_code: editForm.location_code,
        color_code: editForm.color_code,
      };

      const response = await fetch(
        process.env.REACT_APP_BASEURL + "deliver/locations/update",
        {
          method: "POST",
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();
      if (result && result.success) {
        toast.success(result.message || "Location updated");
        this.handleEditClose();
        this.refreshGrid();
      } else {
        throw new Error(result && result.message ? result.message : "Failed to update location");
      }
    } catch (error) {
      console.error("Error updating location:", error);
      toast.error(error.message || "Failed to update location");
      this.setState({ savingEdit: false });
    }
  };

  handleConfirmDelete = async () => {
    if (!this.state.selectedLocation || !this.state.selectedLocation.id) {
      toast.error(this.props.t("Location.CodeGeneration.DeleteError"));
      this.handleDeleteDialogClose();
      return;
    }

    const locationId = this.state.selectedLocation.id;
    this.setState({ deleting: true });

    try {
      const token = JSON.parse(sessionStorage.getItem("user")).signInUserSession.idToken.jwtToken;
      
      const response = await fetch(
        process.env.REACT_APP_BASEURL + "deliver/locations/delete",
        {
          method: "POST",
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          body: JSON.stringify({
            location_id: locationId,
          }),
        }
      );

      let result;
      try {
        result = await response.json();
      } catch (jsonError) {
        const textError = await response.text();
        throw new Error(textError || `HTTP error! status: ${response.status}`);
      }

      if (result && result.error) {
        const errorMessage = result.error.message || result.error.code || this.props.t("Location.CodeGeneration.DeleteError");
        throw new Error(errorMessage);
      }

      if (result && result.success === true) {
        toast.success(
          result.message || this.props.t("Location.CodeGeneration.DeleteSuccess")
        );
        
        this.setState({
          deleting: false,
          deleteDialogOpen: false,
          selectedLocation: null,
        });

        this.refreshGrid();
      } else {
        const errorMessage = result && result.message 
          ? result.message 
          : this.props.t("Location.CodeGeneration.DeleteError");
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error("Error deleting location:", error);
      this.setState({ deleting: false, deleteDialogOpen: false });
      const errorMessage = error.message || this.props.t("Location.CodeGeneration.DeleteError");
      toast.error(errorMessage);
    }
  };

  handleGeneratePDF = async (forAll = false) => {
    if (!this.state.selectedBoxId) {
      toast.error(this.props.t("Location.CodeGeneration.BoxIdRequired"));
      return;
    }

    this.setState({ generatingPDF: true });

    try {
      const token = JSON.parse(sessionStorage.getItem("user")).signInUserSession.idToken.jwtToken;
      
      const requestBody = {
        box_id: this.state.selectedBoxId,
      };

      if (!forAll && this.state.selectedLocation) {
        requestBody.location_ids = [this.state.selectedLocation.id];
      }

      if (this.state.search && this.state.search.trim() !== "") {
        requestBody.search = this.state.search.trim();
      }

      const response = await fetch(
        process.env.REACT_APP_BASEURL + "deliver/locations/generate-pdf-data",
        {
          method: "POST",
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorText = await response.text();
          try {
            const errorJson = JSON.parse(errorText);
            errorMessage = errorJson.message || errorJson.error || errorMessage;
          } catch (parseError) {
            errorMessage = errorText || errorMessage;
          }
        } catch (e) {
          console.error("Could not parse error response:", e);
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      
      if (!data.success || !data.locations || !Array.isArray(data.locations)) {
        throw new Error(data.message || "Invalid response from server");
      }

      await generateLocationCodesPDF(data.locations, this.state.selectedBoxId);

      this.setState({ generatingPDF: false });
      toast.success(
        this.props.t("Location.CodeGeneration.PDFGeneratedSuccess")
      );
    } catch (error) {
      console.error("Error generating PDF:", error);
      this.setState({ generatingPDF: false });
      toast.error(
        this.props.t("Location.CodeGeneration.PDFGenerationError")
      );
    }
  };

  handleAddLocationClick = () => {
    if (!this.state.selectedBoxId) {
      toast.error(this.props.t("Location.CodeGeneration.BoxIdRequired"));
      return;
    }
    this.setState({ addLocationDialogOpen: true });
  };

  handleAddLocationDialogClose = () => {
    this.setState({ addLocationDialogOpen: false });
  };

  handleLocationGenerated = () => {
    this.refreshGrid();
    this.handleAddLocationDialogClose();
  };

  handleGeneratePDFDialogOpen = () => {
    if (!this.state.selectedBoxId) {
      toast.error(this.props.t("Location.CodeGeneration.BoxIdRequired"));
      return;
    }
    this.setState({ 
      generatePDFDialogOpen: true,
      pdfRangeForm: {
        letterRangeStart: "",
        letterRangeEnd: "",
        numberRangeStart: "",
        numberRangeEnd: "",
      },
    });
  };

  handleGeneratePDFDialogClose = () => {
    this.setState({ 
      generatePDFDialogOpen: false,
      pdfRangeForm: {
        letterRangeStart: "",
        letterRangeEnd: "",
        numberRangeStart: "",
        numberRangeEnd: "",
      },
      generatingPDFFromRange: false,
    });
  };

  handlePDFRangeFormChange = (field) => (event) => {
    const value = event.target.value;
    this.setState((prev) => ({
      pdfRangeForm: {
        ...prev.pdfRangeForm,
        [field]: value,
      },
    }));
  };

  filterLocationsByRange = (locations, letterRangeStart, letterRangeEnd, numberRangeStart, numberRangeEnd) => {
    const letterStart = letterRangeStart.toUpperCase().charCodeAt(0);
    const letterEnd = letterRangeEnd.toUpperCase().charCodeAt(0);
    const numStart = parseInt(numberRangeStart, 10);
    const numEnd = parseInt(numberRangeEnd, 10);

    return locations.filter((location) => {
      const locationCode = (location.location || "").toUpperCase().trim();
      const match = locationCode.match(/^([A-Z])(\d+)$/);
      
      if (!match) {
        return false;
      }

      const locationLetter = match[1].charCodeAt(0);
      const locationNumber = parseInt(match[2], 10);
      const letterInRange = locationLetter >= letterStart && locationLetter <= letterEnd;
      const numberInRange = locationNumber >= numStart && locationNumber <= numEnd;

      return letterInRange && numberInRange;
    });
  };

  fetchAllLocations = async () => {
    const { selectedBoxId } = this.state;
    
    if (!selectedBoxId) {
      throw new Error(this.props.t("Location.CodeGeneration.BoxIdRequired"));
    }

    try {
      const token = JSON.parse(sessionStorage.getItem("user")).signInUserSession.idToken.jwtToken;
      const requestBody = {
        box_id: selectedBoxId,
      };

      const response = await fetch(
        process.env.REACT_APP_BASEURL + "deliver/locations/generate-pdf-data",
        {
          method: "POST",
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorText = await response.text();
          try {
            const errorJson = JSON.parse(errorText);
            errorMessage = errorJson.message || errorJson.error || errorMessage;
          } catch (parseError) {
            errorMessage = errorText || errorMessage;
          }
        } catch (e) {
          console.error("Could not parse error response:", e);
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      
      if (!data.success || !data.locations || !Array.isArray(data.locations)) {
        throw new Error(data.message || "Invalid response from server");
      }

      return data.locations;
    } catch (error) {
      console.error("Error fetching locations:", error);
      throw error;
    }
  };

  handleGeneratePDFFromRange = async () => {
    const { pdfRangeForm, selectedBoxId } = this.state;
    const { letterRangeStart, letterRangeEnd, numberRangeStart, numberRangeEnd } = pdfRangeForm;

    if (!selectedBoxId) {
      toast.error(this.props.t("Location.CodeGeneration.BoxIdRequired"));
      return;
    }

    if (!letterRangeStart || !letterRangeEnd || !numberRangeStart || !numberRangeEnd) {
      toast.error(this.props.t("Location.CodeGeneration.RangeRequired"));
      return;
    }

    if (!/^[A-Za-z]$/.test(letterRangeStart) || !/^[A-Za-z]$/.test(letterRangeEnd)) {
      toast.error(this.props.t("Location.CodeGeneration.InvalidRangeChar"));
      return;
    }

    if (letterRangeStart.localeCompare(letterRangeEnd) > 0) {
      toast.error(this.props.t("Location.CodeGeneration.InvalidLetterRange"));
      return;
    }

    const startNum = parseInt(numberRangeStart, 10);
    const endNum = parseInt(numberRangeEnd, 10);
    if (isNaN(startNum) || isNaN(endNum)) {
      toast.error(this.props.t("Location.CodeGeneration.InvalidNumberRange"));
      return;
    }

    if (startNum < 1 || endNum < 1) {
      toast.error(this.props.t("Location.CodeGeneration.NumberRangeOutOfBounds"));
      return;
    }

    if (startNum > endNum) {
      toast.error(this.props.t("Location.CodeGeneration.InvalidNumberRange"));
      return;
    }

    this.setState({ generatingPDFFromRange: true });

    try {
      const allLocations = await this.fetchAllLocations();

      if (allLocations.length === 0) {
        toast.error(this.props.t("Location.CodeGeneration.NoLocationsFound") || "No locations found");
        this.setState({ generatingPDFFromRange: false });
        return;
      }

      const filteredLocations = this.filterLocationsByRange(
        allLocations,
        letterRangeStart,
        letterRangeEnd,
        numberRangeStart,
        numberRangeEnd
      );

      if (filteredLocations.length === 0) {
        toast.error(this.props.t("Location.CodeGeneration.RangeIncorrect") || "Range is incorrect");
        this.setState({ generatingPDFFromRange: false });
        return;
      }

      const letterStart = letterRangeStart.toUpperCase().charCodeAt(0);
      const letterEnd = letterRangeEnd.toUpperCase().charCodeAt(0);
      const numStart = parseInt(numberRangeStart, 10);
      const numEnd = parseInt(numberRangeEnd, 10);

      const expectedLocations = [];
      for (let letterCode = letterStart; letterCode <= letterEnd; letterCode++) {
        for (let num = numStart; num <= numEnd; num++) {
          expectedLocations.push(String.fromCharCode(letterCode) + num);
        }
      }

      const foundLocationCodes = filteredLocations.map(loc => {
        return (loc.location || "").toUpperCase().trim();
      });

      const missingLocations = expectedLocations.filter(expected => !foundLocationCodes.includes(expected));

      if (missingLocations.length > 0) {
        toast.error(this.props.t("Location.CodeGeneration.RangeIncorrect") || "Range is incorrect");
        this.setState({ generatingPDFFromRange: false });
        return;
      }

      await generateLocationCodesPDF(filteredLocations, selectedBoxId);

      this.setState({ generatingPDFFromRange: false });
      toast.success(
        this.props.t("Location.CodeGeneration.PDFGeneratedSuccess", {
          count: filteredLocations.length,
        }) || `PDF generated successfully with ${filteredLocations.length} location(s)`
      );
      
      this.handleGeneratePDFDialogClose();
    } catch (error) {
      console.error("Error generating PDF:", error);
      this.setState({ generatingPDFFromRange: false });
      toast.error(
        error.message || this.props.t("Location.CodeGeneration.PDFGenerationError") || "Error generating PDF"
      );
    }
  };

  handleGeneratePDFForAll = async () => {
    const { selectedBoxId } = this.state;

    if (!selectedBoxId) {
      toast.error(this.props.t("Location.CodeGeneration.BoxIdRequired"));
      return;
    }

    this.setState({ generatingPDFFromRange: true });

    try {
      const allLocations = await this.fetchAllLocations();

      if (allLocations.length === 0) {
        toast.error(this.props.t("Location.CodeGeneration.NoLocationsFound") || "No locations found");
        this.setState({ generatingPDFFromRange: false });
        return;
      }

      await generateLocationCodesPDF(allLocations, selectedBoxId);

      this.setState({ generatingPDFFromRange: false });
      toast.success(
        this.props.t("Location.CodeGeneration.PDFGeneratedSuccess", {
          count: allLocations.length,
        }) || `PDF generated successfully with ${allLocations.length} location(s)`
      );
      
      this.handleGeneratePDFDialogClose();
    } catch (error) {
      console.error("Error generating PDF:", error);
      this.setState({ generatingPDFFromRange: false });
      toast.error(
        error.message || this.props.t("Location.CodeGeneration.PDFGenerationError") || "Error generating PDF"
      );
    }
  };

  render() {
    const { classes, t } = this.props;
    const { 
      deleting, 
      savingEdit,
      totalRecords, 
      deleteDialogOpen, 
      selectedLocation,
      addLocationDialogOpen,
      editDialogOpen,
      editForm,
      boxes,
      selectedBoxId,
      loadingBoxes,
      accountValues,
      selectedAccountId
    } = this.state;
    const selectedBox = boxes.find(box => String(box.id) === String(selectedBoxId));

    const colorOptions = [
      { color: "#f97316", name: "Orange" },
      { color: "#7c3aed", name: "Purple" },
      { color: "#ef4444", name: "Red" },
      { color: "#3b82f6", name: "Blue" },
      { color: "#10b981", name: "Green" },
    ];

    return (
      <div className={classes.root}>
        <Box className={classes.header}>
          <Typography variant="h4" sx={{ fontWeight: 600, color: "#1e293b" }}>
            {t("BinMapping.Title") || "Bin Mapping"}
          </Typography>
        </Box>

        {/* Account, Box Selector and Actions */}
        <Box className={classes.searchContainer}>
          <Box sx={{ minWidth: 220, flex: "1 1 220px", maxWidth: 320 }}>
            <Typography 
              variant="body2" 
              sx={{ 
                mb: 0.5, 
                color: "#475569", 
                fontWeight: 500,
                fontSize: "0.875rem"
              }}
            >
              {t("BinMapping.SelectAccount") || "Select Account"}
            </Typography>
            <SearchableSelect
              label="Account"
              showLabel={false}
              items={(accountValues || []).map((account) => ({
                id: String(account.value),
                name: account.label || "",
              }))}
              value={selectedAccountId ? String(selectedAccountId) : ""}
              onChange={this.handleAccountChange}
              placeholder={
                this.state.loadingAccounts
                  ? "Loading accounts..."
                  : "Search accounts..."
              }
              id="bin-mapping-account-select"
              fullWidth
              variant="outlined"
              disabled={this.state.loadingAccounts}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  backgroundColor: "#ffffff",
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#7c3aed",
                    borderWidth: "2px",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#7c3aed",
                    borderWidth: "2px",
                  },
                },
              }}
            />
          </Box>
          
          <Box sx={{ minWidth: 220, flex: "1 1 220px", maxWidth: 320 }}>
            <Typography 
              variant="body2" 
              sx={{ 
                mb: 0.5, 
                color: "#475569", 
                fontWeight: 500,
                fontSize: "0.875rem"
              }}
            >
              {t("BinMapping.DeliveryLocation") || "Delivery Location"}
            </Typography>
            <SearchableSelect
              label="Location"
              showLabel={false}
              items={(boxes || []).map((box) => ({
                id: String(box.id),
                name: box.name || String(box.id),
              }))}
              value={selectedBoxId ? String(selectedBoxId) : ""}
              onChange={this.handleBoxChange}
              placeholder={
                !selectedAccountId
                  ? (t("BinMapping.SelectAccountFirst") || "Please select an account first")
                  : loadingBoxes
                    ? "Loading boxes..."
                    : boxes.length === 0
                      ? (t("BinMapping.NoCounterBoxes") || "No Counter boxes found")
                      : "Search locations..."
              }
              id="bin-mapping-box-select"
              fullWidth
              variant="outlined"
              disabled={loadingBoxes || !selectedAccountId || boxes.length === 0}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  backgroundColor: "#ffffff",
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#7c3aed",
                    borderWidth: "2px",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#7c3aed",
                    borderWidth: "2px",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#f1f5f9",
                  },
                },
              }}
            />
          </Box>
          
          <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end" }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={this.handleAddLocationClick}
              disabled={!selectedBoxId}
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                padding: "10px 24px",
                fontSize: "0.875rem",
                fontWeight: 600,
                backgroundColor: "#7c3aed",
                boxShadow: "0 2px 4px rgba(124, 58, 237, 0.2)",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: "#6d28d9",
                  boxShadow: "0 4px 8px rgba(124, 58, 237, 0.3)",
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
                "&:disabled": {
                  backgroundColor: "#cbd5e1",
                  color: "#94a3b8",
                  boxShadow: "none",
                  cursor: "not-allowed",
                },
              }}
            >
              {t("BinMapping.AddLocation") || "Add Location"}
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<PictureAsPdfIcon />}
              onClick={this.handleGeneratePDFDialogOpen}
              disabled={!selectedBoxId}
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                padding: "10px 24px",
                fontSize: "0.875rem",
                fontWeight: 600,
                borderColor: "#7c3aed",
                color: "#7c3aed",
                boxShadow: "0 2px 4px rgba(124, 58, 237, 0.1)",
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: "#6d28d9",
                  backgroundColor: "rgba(124, 58, 237, 0.05)",
                  boxShadow: "0 4px 8px rgba(124, 58, 237, 0.2)",
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
                "&:disabled": {
                  borderColor: "#cbd5e1",
                  color: "#94a3b8",
                  boxShadow: "none",
                  cursor: "not-allowed",
                },
              }}
            >
              {t("Location.CodeGeneration.GeneratePDF") || "Generate PDF"}
            </Button>
          </Box>
        </Box>

        {/* Location Codes List */}
        <Box sx={{ mb: 3 }}>
          {totalRecords > 0 && (
            <Typography variant="body2" sx={{ mb: 2, color: "#64748b" }}>
              {totalRecords} {t("Location.CodeGeneration.TotalLocations")}
            </Typography>
          )}
          <ILocalAGGrid
            columnDefs={[
              {
                field: "location",
                headerName: t("Location.CodeGeneration.Location") || "LOCATION",
                cellRenderer: (params) => {
                  const code = params.data || {};
                  const bg = code.color_code || "#000000";
                  return (
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        padding: "6px 12px",
                        borderRadius: "6px",
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        backgroundColor: bg,
                        color: this.getContrastColor(bg),
                      }}
                    >
                      {code.location || "-"}
                    </span>
                  );
                },
              },
              {
                field: "location_code",
                headerName: t("Location.CodeGeneration.LocationCode") || "LOCATION CODE",
                cellStyle: { fontFamily: "monospace", fontWeight: 500 },
              },
              {
                field: "created_on",
                headerName: t("Location.CodeGeneration.CreatedDate") || "CREATED DATE",
                filter: false,
                valueGetter: (p) =>
                  p.data && p.data.created_on
                    ? new Date(p.data.created_on).toLocaleDateString()
                    : "-",
              },
              getActionsColumn({
                headerName: t("Location.CodeGeneration.Actions") || "ACTIONS",
                onActionsClick: (event, params, menuHelpers) =>
                  this.openRowActionsMenu(event, params, menuHelpers),
              }),
            ]}
            defaultColDef={getDefaultColDef()}
            rowModelType="serverSide"
            cacheBlockSize={FilterObj.pageSize || 20}
            paginationPageSize={FilterObj.pageSize || 20}
            paginationPageSizeSelector={[10, 20, 25]}
            rowHeight={52}
            getRowId={(params) => params.data && params.data.id}
            onGridReady={this.onGridReady}
            onPaginationChanged={this.onPaginationChanged}
            getContextMenuItems={this.getContextMenuItems}
            overlayNoRowsMessage={t("Location.CodeGeneration.NoLocationsFound")}
          />
        </Box>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={this.handleDeleteDialogClose}
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-description"
        >
          <DialogTitle id="delete-dialog-title">
            {t("Location.CodeGeneration.DeleteConfirmTitle")}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="delete-dialog-description">
              {t("Location.CodeGeneration.DeleteConfirmMessage")}
              {selectedLocation && (
                <Box component="div" sx={{ mt: 1.5, p: 1, backgroundColor: "#f8fafc", borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {t("Location.CodeGeneration.Location")}: {selectedLocation.location || "-"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#64748b" }}>
                    {t("Location.CodeGeneration.LocationCode")}: {selectedLocation.location_code || "-"}
                  </Typography>
                </Box>
              )}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={this.handleDeleteDialogClose}
              color="primary"
              disabled={deleting}
              sx={{ textTransform: "none" }}
            >
              {t("Location.CodeGeneration.Cancel")}
            </Button>
            <Button
              onClick={this.handleConfirmDelete}
              color="error"
              variant="contained"
              disabled={deleting}
              sx={{ textTransform: "none" }}
            >
              {deleting ? (
                <React.Fragment>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  {t("Location.CodeGeneration.Deleting")}
                </React.Fragment>
              ) : (
                t("Location.CodeGeneration.Delete")
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog
          open={editDialogOpen}
          onClose={this.handleEditClose}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {t("Location.CodeGeneration.EditLocation") || "Edit Location"}
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label={t("Location.CodeGeneration.Location") || "Location"}
                value={editForm.location}
                onChange={this.handleEditFieldChange("location")}
                fullWidth
              />
              <TextField
                label={t("Location.CodeGeneration.LocationCode") || "Location Code"}
                value={editForm.location_code}
                onChange={this.handleEditFieldChange("location_code")}
                fullWidth
              />
              <Box>
                <Typography sx={{ mb: 1, color: "#475569", fontWeight: 500 }}>
                  {t("Location.CodeGeneration.ColorCode") || "Color"}
                </Typography>
                <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                  {colorOptions.map((opt) => (
                    <Box
                      key={opt.color}
                      onClick={() => this.handleEditColorSelect(opt.color)}
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: "8px",
                        backgroundColor: opt.color,
                        border: editForm.color_code === opt.color ? "3px solid #0ea5e9" : "2px solid #e2e8f0",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.2s ease",
                        "&:hover": { transform: "scale(1.05)" },
                      }}
                    >
                      {editForm.color_code === opt.color && (
                        <Typography sx={{ color: "#ffffff", fontWeight: 700 }}>✓</Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.handleEditClose} disabled={savingEdit} sx={{ textTransform: "none" }}>
              {t("Location.CodeGeneration.Cancel") || "Cancel"}
            </Button>
            <Button
              onClick={this.handleEditSave}
              variant="contained"
              disabled={savingEdit}
              startIcon={savingEdit ? <CircularProgress size={18} /> : null}
              sx={{
                textTransform: "none",
                backgroundColor: "#7c3aed",
                boxShadow: "0 2px 4px rgba(124, 58, 237, 0.2)",
                "&:hover": {
                  backgroundColor: "#6d28d9",
                  boxShadow: "0 4px 8px rgba(124, 58, 237, 0.3)",
                },
                "&:disabled": {
                  backgroundColor: "#cbd5e1",
                  color: "#94a3b8",
                  boxShadow: "none",
                },
              }}
            >
              {savingEdit ? (t("Location.CodeGeneration.Saving") || "Saving...") : (t("Location.CodeGeneration.Save") || "Save")}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Location Dialog */}
        <Dialog
          open={addLocationDialogOpen}
          onClose={this.handleAddLocationDialogClose}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "12px",
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: "20px 24px",
              borderBottom: "1px solid #e2e8f0",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: "20px",
                fontWeight: 600,
                color: "#1e293b",
              }}
            >
              {t("BinMapping.AddLocation") || "Add Location"}
            </Typography>
            <IconButton
              onClick={this.handleAddLocationDialogClose}
              size="small"
              sx={{
                color: "#64748b",
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            <BinMappingGeneration
              classes={classes}
              box={selectedBox}
              onStateChange={() => {}}
              onLocationGenerated={this.handleLocationGenerated}
            />
          </DialogContent>
        </Dialog>

        {/* Generate PDF Dialog */}
        <Dialog
          open={this.state.generatePDFDialogOpen}
          onClose={this.handleGeneratePDFDialogClose}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "12px",
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: "20px 24px",
              borderBottom: "1px solid #e2e8f0",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: "20px",
                fontWeight: 600,
                color: "#1e293b",
              }}
            >
              {t("Location.CodeGeneration.GeneratePDF") || "Generate PDF"}
            </Typography>
            <IconButton
              onClick={this.handleGeneratePDFDialogClose}
              size="small"
              disabled={this.state.generatingPDFFromRange}
              sx={{
                color: "#64748b",
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            <Box sx={{ p: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      marginBottom: "16px", 
                      color: "#475569", 
                      fontWeight: 500, 
                      fontSize: "0.875rem" 
                    }}
                  >
                    {t("Location.CodeGeneration.LetterRange")}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label={t("Location.CodeGeneration.LetterRangeStart")}
                    value={this.state.pdfRangeForm.letterRangeStart}
                    onChange={this.handlePDFRangeFormChange("letterRangeStart")}
                    inputProps={{ maxLength: 1 }}
                    disabled={this.state.generatingPDFFromRange}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label={t("Location.CodeGeneration.LetterRangeEnd")}
                    value={this.state.pdfRangeForm.letterRangeEnd}
                    onChange={this.handlePDFRangeFormChange("letterRangeEnd")}
                    inputProps={{ maxLength: 1 }}
                    disabled={this.state.generatingPDFFromRange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      marginTop: "16px", 
                      marginBottom: "16px", 
                      color: "#475569", 
                      fontWeight: 500, 
                      fontSize: "0.875rem" 
                    }}
                  >
                    {t("BinMapping.NumberRange") || "Number Range (1 or greater)"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label={t("BinMapping.NumberRangeStart") || "Number Range Start (1 or greater)"}
                    placeholder={t("BinMapping.NumberRangeStartPlaceholder") || "e.g., 1, 10, 100"}
                    value={this.state.pdfRangeForm.numberRangeStart}
                    onChange={this.handlePDFRangeFormChange("numberRangeStart")}
                    disabled={this.state.generatingPDFFromRange}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label={t("BinMapping.NumberRangeEnd") || "Number Range End (1 or greater)"}
                    placeholder={t("BinMapping.NumberRangeEndPlaceholder") || "e.g., 10, 100, 1000"}
                    value={this.state.pdfRangeForm.numberRangeEnd}
                    onChange={this.handlePDFRangeFormChange("numberRangeEnd")}
                    disabled={this.state.generatingPDFFromRange}
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 3, display: "flex", gap: 2, justifyContent: "flex-end" }}>
                <Button
                  onClick={this.handleGeneratePDFForAll}
                  variant="contained"
                  disabled={this.state.generatingPDFFromRange}
                  startIcon={this.state.generatingPDFFromRange ? <CircularProgress size={20} /> : <PictureAsPdfIcon />}
                  sx={{
                    textTransform: "none",
                    borderRadius: "8px",
                    padding: "10px 24px",
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    backgroundColor: "#7c3aed",
                    boxShadow: "0 2px 4px rgba(124, 58, 237, 0.2)",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: "#6d28d9",
                      boxShadow: "0 4px 8px rgba(124, 58, 237, 0.3)",
                      transform: "translateY(-1px)",
                    },
                    "&:active": {
                      transform: "translateY(0)",
                    },
                    "&:disabled": {
                      backgroundColor: "#cbd5e1",
                      color: "#94a3b8",
                      boxShadow: "none",
                      cursor: "not-allowed",
                    },
                  }}
                >
                  {this.state.generatingPDFFromRange
                    ? t("Location.CodeGeneration.GeneratingPDF")
                    : t("Location.CodeGeneration.GenerateAll")}
                </Button>
                <Button
                  onClick={this.handleGeneratePDFFromRange}
                  variant="contained"
                  disabled={
                    this.state.generatingPDFFromRange ||
                    !this.state.pdfRangeForm.letterRangeStart ||
                    !this.state.pdfRangeForm.letterRangeEnd ||
                    !this.state.pdfRangeForm.numberRangeStart ||
                    !this.state.pdfRangeForm.numberRangeEnd
                  }
                  startIcon={this.state.generatingPDFFromRange ? <CircularProgress size={20} /> : <PictureAsPdfIcon />}
                  sx={{
                    textTransform: "none",
                    borderRadius: "8px",
                    padding: "10px 24px",
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    backgroundColor: "#7c3aed",
                    boxShadow: "0 2px 4px rgba(124, 58, 237, 0.2)",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: "#6d28d9",
                      boxShadow: "0 4px 8px rgba(124, 58, 237, 0.3)",
                      transform: "translateY(-1px)",
                    },
                    "&:active": {
                      transform: "translateY(0)",
                    },
                    "&:disabled": {
                      backgroundColor: "#cbd5e1",
                      color: "#94a3b8",
                      boxShadow: "none",
                      cursor: "not-allowed",
                    },
                  }}
                >
                  {this.state.generatingPDFFromRange
                    ? t("Location.CodeGeneration.GeneratingPDF")
                    : t("Location.CodeGeneration.GeneratePDFForRange")}
                </Button>
              </Box>
            </Box>
          </DialogContent>
        </Dialog>
      </div>
    );
  }
}

export default withStyles(styles)(withTranslation()(BinMapping));



// WEBPACK FOOTER //
// ./src/components/bin-mapping/index.js